import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../layout/Layout';
import AuthContext from '../../context/AuthContext';
import { networkApi, organisationApi, panoptesApi, securityCamerasApi } from '../../services/api';
import { buildNvrCameraMatrices, mergePanopticFeedCameras } from './PanopticCockpit.feeds';
import { getPanopticLauncherLinks, PANOPTIC_APP_LAUNCHER_TEST_IDS } from './PanopticCockpit.launcher';
import { getPanopticLinkStateView } from './PanopticCockpit.linkState';
import {
  resolvePanopticOrganisationSelection,
  selectPanopticOrganisations,
  shouldShowPanopticOrganisationSelector,
} from './PanopticCockpit.orgScope';
import { buildPanopticSourceView, getPanopticConnectionState } from './PanopticCockpit.source';
import {
  buildActiveWatchRulesSummary,
  buildWatchRuleError,
  normalizeActiveWatchRuleState,
  normalizeWatchRulePayload,
} from './PanopticCockpit.watchRules';
import PanopticRecognitionReview from './PanopticRecognitionReview';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import './PanopticCockpit.css';

const sampleCameras = [
  {
    id: 'cam-foyer',
    name: 'Foyer North',
    room: 'Main entrance',
    site: 'Studio A',
    status: 'online',
    signal: 'Live',
    lastSeen: '34 s ago',
    labels: ['person', 'door'],
    unauthorized: false,
  },
  {
    id: 'cam-floor',
    name: 'Dance Floor East',
    room: 'Studio 1',
    site: 'Studio A',
    status: 'alert',
    signal: 'Motion',
    lastSeen: '2 min ago',
    labels: ['person', 'zone'],
    unauthorized: false,
  },
  {
    id: 'cam-yard',
    name: 'Rear Yard',
    room: 'External',
    site: 'Studio A',
    status: 'degraded',
    signal: 'Low FPS',
    lastSeen: '7 min ago',
    labels: ['vehicle'],
    unauthorized: false,
  },
  {
    id: 'cam-office',
    name: 'Office Store',
    room: 'Restricted',
    site: 'Studio A',
    status: 'restricted',
    signal: 'Locked',
    lastSeen: 'Hidden',
    labels: ['authorized only'],
    unauthorized: true,
  },
];

const sampleReviews = [
  {
    id: 'rv-1820',
    title: 'Unusual motion near main entrance',
    camera: 'Foyer North',
    severity: 'critical',
    age: '4 min',
    state: 'new',
  },
  {
    id: 'rv-1817',
    title: 'Detector confidence dip in Studio 1',
    camera: 'Dance Floor East',
    severity: 'warning',
    age: '18 min',
    state: 'triage',
  },
  {
    id: 'rv-1809',
    title: 'Rear Yard stream recovered',
    camera: 'Rear Yard',
    severity: 'info',
    age: '43 min',
    state: 'closed',
  },
];

const runtimeFallback = {
  detector: 'Frigate detector',
  detectorState: 'degraded',
  accelerator: 'OpenVINO / CPU fallback',
  discovery: 'Ready',
  sync: '2 pending config checks',
  audit: '3 sensitive actions today',
};

const emptyState = {
  cameras: [],
  reviews: [],
  runtime: {
    detector: 'Detector state unavailable',
    detectorState: 'unknown',
    accelerator: 'Unknown accelerator',
    discovery: 'Discovery not started',
    sync: 'No sync data',
    audit: 'No audit highlights',
  },
};

const extractData = (response) => response?.data?.data ?? response?.data ?? null;

const asArray = (value, keys = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  for (const key of keys) {
    const candidate = value?.[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const normalizeStatus = (value) => {
  const status = String(value || '').toLowerCase();
  if (['online', 'healthy', 'active', 'ok', 'up'].includes(status)) {
    return 'online';
  }
  if (['critical', 'alert', 'warning', 'anomaly'].includes(status)) {
    return 'alert';
  }
  if (['offline', 'down', 'missing'].includes(status)) {
    return 'offline';
  }
  if (['degraded', 'slow', 'unknown'].includes(status)) {
    return 'degraded';
  }
  return status || 'unknown';
};

const formatCount = (value) => {
  if (!Number.isFinite(value)) {
    return '0';
  }
  return new Intl.NumberFormat('en-GB').format(value);
};

const formatTime = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const buildCameraFromDevice = (device, index) => {
  const hostname = device.hostname || device.name || device.label || `Camera ${index + 1}`;
  const ipAddress = device.ipAddress || device.ip_address || device.primaryIp || device.ip || '';
  const deviceType = String(device.type || device.deviceType || device.device_type || device.category || '').toLowerCase();
  const labels = [
    device.vendor || device.manufacturer || deviceType || 'camera',
    ipAddress,
  ].filter(Boolean).slice(0, 2);

  return {
    id: device.id || device.uuid || device.macAddress || device.mac_address || `camera-${index}`,
    name: hostname,
    room: device.roomName || device.room || device.location || 'Unmapped room',
    site: device.site || device.building || 'TestDeploy estate',
    status: normalizeStatus(device.healthStatus || device.status || device.state),
    signal: device.lastSignal || device.reachability || 'Observed',
    lastSeen: formatTime(device.lastSeenAt || device.last_seen_at || device.last_seen || device.updatedAt),
    labels,
    unauthorized: Boolean(device.unauthorized || device.mediaUnauthorized),
  };
};

const mapCameraPayload = (cameraPayload, devicesPayload) => {
  const cameras = asArray(cameraPayload, ['cameras', 'items', 'results']);
  if (cameras.length > 0) {
    return cameras.slice(0, 8).map((camera, index) => ({
      id: camera.id || camera.uuid || camera.cameraId || `camera-${index}`,
      name: camera.name || camera.label || camera.cameraName || `Camera ${index + 1}`,
      room: camera.room || camera.roomName || camera.location || 'Unmapped room',
      site: camera.site || camera.building || 'TestDeploy estate',
      status: normalizeStatus(camera.status || camera.health || camera.state),
      signal: camera.signal || camera.lastEvent || camera.detectorState || 'Observed',
      lastSeen: formatTime(camera.lastSeenAt || camera.last_seen_at || camera.last_seen || camera.updatedAt),
      labels: asArray(camera.labels).slice(0, 3),
      unauthorized: Boolean(camera.unauthorized || camera.mediaUnauthorized),
    }));
  }

  const devices = asArray(devicesPayload, ['devices', 'items', 'results']);
  const cameraLikeDevices = devices.filter((device) => {
    const text = [
      device.type,
      device.deviceType,
      device.device_type,
      device.category,
      device.name,
      device.hostname,
      device.vendor,
      device.manufacturer,
      device.manufacturer_oui,
    ].filter(Boolean).join(' ').toLowerCase();
    return /camera|nvr|onvif|frigate|hikvision|reolink|rtsp/.test(text);
  });

  return cameraLikeDevices.slice(0, 8).map(buildCameraFromDevice);
};

const normalizeFeedEntry = (feed = {}) => {
  const explicitOnline = feed.isOnline ?? feed.is_online;
  const status = String(feed.status || feed.state || '').trim().toLowerCase();
  const name = String(feed.name || '').trim();
  const hostname = String(feed.device?.hostname ?? feed.device_hostname ?? '').trim();
  const ipAddress = feed.ipAddress ?? feed.ip_address ?? feed.device?.ipAddress ?? feed.device?.ip_address ?? '';
  return {
    id: feed.id,
    deviceId: feed.deviceId ?? feed.device_id,
    name,
    nameKey: name.toLowerCase(),
    hostname,
    hostnameKey: hostname.toLowerCase(),
    ipAddress,
    isOnline: explicitOnline === undefined ? status !== 'offline' : Boolean(explicitOnline),
  };
};

const buildCameraFromSecurityFeed = (feed) => {
  const label = feed.hostname || feed.name || feed.ipAddress || `Security feed ${feed.id}`;
  return {
    id: `security-feed-${feed.id}`,
    name: label,
    room: feed.ipAddress || 'Security feed',
    site: 'StudioAEye',
    status: feed.isOnline ? 'online' : 'degraded',
    signal: feed.isOnline ? 'Live snapshot' : 'Linked feed',
    lastSeen: feed.isOnline ? 'Connected feed' : 'Feed unavailable',
    labels: ['security', feed.ipAddress].filter(Boolean).slice(0, 3),
    unauthorized: false,
    feedId: feed.id,
    feedOnline: feed.isOnline,
  };
};

// MP004-F0423: join cockpit telemetry cameras to org security feeds (id → name → IP)
// so matrix tiles can load snapshots through the PanOptic service-link pipeline.
const attachSecurityFeeds = (cameras, feedsPayload) => {
  const feeds = asArray(feedsPayload, ['feeds', 'items', 'results']).map(normalizeFeedEntry);
  if (feeds.length === 0) {
    return cameras;
  }

  const byId = new Map();
  const byName = new Map();
  const byIp = new Map();
  feeds.forEach((feed) => {
    if (feed.id !== undefined && feed.id !== null && !byId.has(String(feed.id))) {
      byId.set(String(feed.id), feed);
    }
    if (feed.deviceId !== undefined && feed.deviceId !== null && !byId.has(String(feed.deviceId))) {
      byId.set(String(feed.deviceId), feed);
    }
    if (feed.nameKey && !byName.has(feed.nameKey)) {
      byName.set(feed.nameKey, feed);
    }
    if (feed.hostnameKey && !byName.has(feed.hostnameKey)) {
      byName.set(feed.hostnameKey, feed);
    }
    if (feed.ipAddress && !byIp.has(feed.ipAddress)) {
      byIp.set(feed.ipAddress, feed);
    }
  });

  const attachedFeedIds = new Set();
  const attached = cameras.map((camera) => {
    const feed = byId.get(String(camera.id))
      || byName.get(String(camera.name || '').trim().toLowerCase())
      || (camera.labels || []).map((label) => byIp.get(label)).find(Boolean)
      || null;
    if (!feed) {
      return camera;
    }
    attachedFeedIds.add(String(feed.id));
    return { ...camera, feedId: feed.id, feedOnline: feed.isOnline };
  });

  const appended = feeds
    .filter((feed) => feed.id !== undefined && feed.id !== null && !attachedFeedIds.has(String(feed.id)))
    .map(buildCameraFromSecurityFeed);

  return [...attached, ...appended];
};

const mapReviewPayload = (anomaliesPayload, cameras) => {
  const anomalies = asArray(anomaliesPayload, ['anomalies', 'items', 'results']);
  if (anomalies.length > 0) {
    return anomalies.slice(0, 5).map((item, index) => ({
      id: item.id || item.uuid || `review-${index}`,
      title: item.title || item.summary || item.message || item.kind || 'Review item',
      camera: item.cameraName || item.deviceName || item.sourceName || cameras[index % Math.max(1, cameras.length)]?.name || 'Camera',
      severity: normalizeStatus(item.severity || item.priority || item.status),
      age: formatTime(item.createdAt || item.created_at || item.updatedAt),
      state: item.state || item.status || 'new',
    }));
  }

  return cameras.length > 0 ? sampleReviews : [];
};

const mapRuntimePayload = (summaryPayload, cameraHealthPayload) => {
  const summary = summaryPayload || {};
  const cameraHealth = cameraHealthPayload || {};
  const detectorState = normalizeStatus(
    summary.detectorStatus
      || summary.detector_status
      || cameraHealth.detectorStatus
      || cameraHealth.status
      || summary.status,
  );

  return {
    detector: summary.detector || cameraHealth.detector || 'Frigate detector',
    detectorState,
    accelerator: summary.accelerator || cameraHealth.accelerator || cameraHealth.runtime || runtimeFallback.accelerator,
    discovery: summary.discoveryStatus || summary.discovery_status || 'Ready for discovery',
    sync: summary.syncDrift || summary.sync_drift || summary.pendingSync || runtimeFallback.sync,
    audit: summary.auditHighlight || summary.audit_highlight || runtimeFallback.audit,
  };
};

const buildErrorText = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return 'PanOptic data is protected. The cockpit is showing restricted operational states until authorization is refreshed.';
  }
  if (status) {
    return `PanOptic data is degraded because one live endpoint returned HTTP ${status}.`;
  }
  return 'PanOptic data is degraded because the live telemetry endpoints are not reachable.';
};

function PanopticMetric({ label, value, detail, onClick }) {
  return (
    <button className="panoptic-metric" type="button" onClick={onClick} data-testid={`panoptic-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </button>
  );
}

function StatusPill({ status, children }) {
  return (
    <span className={`panoptic-status panoptic-status-${normalizeStatus(status)}`}>
      {children || status}
    </span>
  );
}

function CameraTile({ camera, orgId, onInspect }) {
  const [visible, setVisible] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const tileRef = useRef(null);
  const objectUrlRef = useRef('');
  // MP004-F0423: unauthorized cameras must never request media; offline or
  // unmatched cameras keep the placeholder.
  const canSnapshot = Boolean(orgId && camera.feedId && !camera.unauthorized && camera.feedOnline !== false);

  useEffect(() => {
    if (!canSnapshot) {
      return undefined;
    }
    const element = tileRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '160px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, [canSnapshot]);

  useEffect(() => {
    if (!canSnapshot || !visible) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snapshotParams = {
          org_id: orgId,
          width: 320,
          quality: 70,
        };
        const response = camera.feedSource === 'panoptic-org'
          ? await panoptesApi.getOrgFeedSnapshot(orgId, camera.feedId, snapshotParams)
          : await securityCamerasApi.getSnapshot(camera.feedId, snapshotParams);
        if (cancelled) {
          return;
        }
        const nextUrl = URL.createObjectURL(response.data);
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = nextUrl;
        setSnapshotUrl(nextUrl);
      } catch (snapshotError) {
        // Snapshot is a progressive enhancement; the placeholder remains on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [camera.feedId, canSnapshot, orgId, visible]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  return (
    <article ref={tileRef} className={`panoptic-camera-tile is-${normalizeStatus(camera.status)}`} data-testid="panoptic-camera-tile">
      <button className="panoptic-camera-visual" type="button" onClick={() => onInspect(camera)} aria-label={`Inspect ${camera.name}`}>
        {snapshotUrl ? (
          <img
            className="panoptic-camera-thumbnail"
            src={snapshotUrl}
            alt={`${camera.name} snapshot`}
            data-testid="panoptic-camera-thumbnail-image"
          />
        ) : (
          <span className="panoptic-camera-scanline" aria-hidden="true" />
        )}
        <span className="panoptic-camera-lock">{camera.unauthorized ? 'Restricted media' : camera.signal}</span>
      </button>
      <div className="panoptic-camera-body">
        <div>
          <h3>{camera.name}</h3>
          <p>{camera.room} · {camera.site}</p>
        </div>
        <StatusPill status={camera.status}>{camera.status}</StatusPill>
      </div>
      <div className="panoptic-camera-foot">
        <span>{camera.lastSeen}</span>
        <div className="panoptic-chip-row">
          {(camera.labels?.length ? camera.labels : ['camera']).slice(0, 3).map((label) => (
            <span className="panoptic-chip" key={`${camera.id}-${label}`}>{label}</span>
          ))}
        </div>
      </div>
    </article>
  );
}

function ActiveWatchRulesCard({
  camera,
  ruleState,
  onAddRule,
  onRemoveRule,
  onRefresh,
}) {
  const rules = ruleState?.rules || [];
  const activeRules = rules.filter((rule) => rule.enabled);
  const busy = ruleState?.loading || ruleState?.mutating;

  return (
    <section className="panoptic-watch-card" data-testid="T_PANOPTIC_WEB_125_active_watch_rules">
      <div className="panoptic-inline-head">
        <div>
          <h3>Active Watch Rules</h3>
          <p>{camera?.feedId ? `Feed ${camera.feedId}` : 'Feed rule management needs a linked feed ID.'}</p>
        </div>
        <StatusPill status={ruleState?.error ? 'degraded' : activeRules.length ? 'critical' : 'unknown'}>
          {ruleState?.error ? 'Sync error' : activeRules.length ? 'Active' : 'No active rules'}
        </StatusPill>
      </div>

      {ruleState?.error && (
        <div className="panoptic-inline-warning" data-testid="T_PANOPTIC_WEB_125_rule_sync_error">
          <strong>{ruleState.error}</strong>
          {ruleState.needsRefresh && (
            <button type="button" onClick={onRefresh} data-testid="T_PANOPTIC_WEB_174_refresh_rules">
              Refresh rules
            </button>
          )}
        </div>
      )}

      <div className="panoptic-rule-list">
        {rules.length === 0 ? (
          <div className="panoptic-inline-state">
            <strong>No rules returned</strong>
            <span>Refresh once the linked node exposes watch_rules.list for this feed.</span>
          </div>
        ) : rules.map((rule) => (
          <article className="panoptic-rule-row" key={`${rule.id}-${rule.type}`} data-testid="T_PANOPTIC_WEB_125_rule_row">
            <div>
              <strong>{rule.label}</strong>
              <span>{rule.objects} · {rule.schedule} · {rule.sync}</span>
            </div>
            <StatusPill status={rule.enabled ? rule.severity : 'unknown'}>{rule.state}</StatusPill>
            <button
              type="button"
              onClick={() => onRemoveRule(rule)}
              disabled={busy || !camera?.feedId}
              data-testid="T_PANOPTIC_WEB_126_remove_out_of_hours"
            >
              Remove
            </button>
          </article>
        ))}
      </div>

      <div className="panoptic-watch-actions">
        <button
          type="button"
          className="panoptic-primary-button"
          onClick={onAddRule}
          disabled={busy || !camera?.feedId}
          data-testid="T_PANOPTIC_WEB_126_add_out_of_hours"
        >
          Add Out of hours object watch
        </button>
        <p>Feed provisioning is confirmed by the backend/node before the rule is reported active.</p>
      </div>
    </section>
  );
}

function buildHostedFeedUrl(orgId, feedId) {
  if (!orgId || !feedId) {
    return '';
  }
  return `/api/v1/orgs/${encodeURIComponent(orgId)}/feeds/${encodeURIComponent(feedId)}/snapshot`;
}

function sanitizeRuleFeedUrl(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }
  try {
    const parsed = new URL(rawValue, window.location.origin);
    parsed.username = '';
    parsed.password = '';
    return parsed.href.startsWith(window.location.origin)
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : parsed.href;
  } catch {
    return rawValue.replace(/:\/\/[^/@\s]+@/g, '://');
  }
}

function ActiveWatchRuleThumbnail({ orgId, feedId, thumbnailUrl }) {
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const objectUrlRef = useRef('');

  useEffect(() => {
    if (!orgId || !feedId || !thumbnailUrl) {
      return undefined;
    }

    let cancelled = false;
    setUnavailable(false);
    setSnapshotUrl('');
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }

    (async () => {
      try {
        const response = await panoptesApi.getOrgFeedSnapshot(orgId, feedId, {
          width: 160,
          quality: 70,
        });
        if (cancelled) {
          return;
        }
        const nextUrl = URL.createObjectURL(response.data);
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = nextUrl;
        setSnapshotUrl(nextUrl);
      } catch {
        if (!cancelled) {
          setUnavailable(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [feedId, orgId, thumbnailUrl]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  return (
    <div
      className={`panoptic-rule-feed-thumb ${unavailable ? 'is-unavailable' : ''}`}
      data-testid="T_PANOPTIC_WEB_168_rule_feed_thumbnail"
      data-src={thumbnailUrl}
    >
      {snapshotUrl ? (
        <img
          src={snapshotUrl}
          alt={`Feed thumbnail for ${feedId}`}
          loading="lazy"
          data-testid="T_PANOPTIC_WEB_168_rule_feed_thumbnail_image"
        />
      ) : null}
      <span>{unavailable ? 'Preview unavailable' : 'Loading preview'}</span>
    </div>
  );
}

function ActiveWatchRulesSummary({ activeRuleState, feedRuleStates, onRefresh, orgId }) {
  const summary = buildActiveWatchRulesSummary(activeRuleState, feedRuleStates);
  const activeRules = summary.rules;
  const canRefresh = Boolean(onRefresh) && ['error', 'stale'].includes(summary.state);
  const emptyCopy = {
    loading: {
      title: 'Checking active watch rules',
      body: 'Server Link active-rule state is being refreshed.',
    },
    error: {
      title: 'Active watch rules unavailable',
      body: summary.error || 'Server Link active-rule state could not be loaded.',
    },
    stale: {
      title: 'Active watch rules stale',
      body: 'Backend returned cached rule state. The page will keep checking for a fresh update.',
    },
    unqueried: {
      title: 'Active watch rules not queried',
      body: 'Select an organisation to load Server Link active-rule state.',
    },
    'fresh-empty': {
      title: 'No active watch rules reported',
      body: 'Backend returned a fresh empty active-rule list.',
    },
  }[summary.state] || null;

  return (
    <section className="panoptic-watch-summary" data-testid="T_PANOPTIC_WEB_126_review_rule_summary">
      <div className="panoptic-inline-head compact">
        <div>
          <h3>Active Watch Rules</h3>
          <p>Mirrors the org active-rule summary and selected feed drawer.</p>
        </div>
        {canRefresh && (
          <button type="button" onClick={onRefresh} data-testid="T_PANOPTIC_WEB_168_refresh_active_rules">
            Refresh
          </button>
        )}
      </div>
      {activeRules.length === 0 ? (
        <div className="panoptic-inline-state" data-testid="T_PANOPTIC_WEB_168_active_watch_summary_state">
          <strong>{emptyCopy.title}</strong>
          <span>{emptyCopy.body}</span>
        </div>
      ) : activeRules.map((rule) => {
        const feedUrl = sanitizeRuleFeedUrl(rule.feedUrl || buildHostedFeedUrl(orgId, rule.feedId));
        const thumbnailUrl = buildHostedFeedUrl(orgId, rule.feedId);
        return (
          <div
            className={`panoptic-rule-summary-row ${thumbnailUrl ? 'has-thumbnail' : 'no-thumbnail'}`}
            key={`${rule.feedId}-${rule.id}`}
          >
            {thumbnailUrl ? (
              <ActiveWatchRuleThumbnail orgId={orgId} feedId={rule.feedId} thumbnailUrl={thumbnailUrl} />
            ) : null}
            <div className="panoptic-rule-summary-body">
              <StatusPill status={rule.severity}>{rule.label}</StatusPill>
              <span>{rule.feedId} · {rule.schedule}</span>
              {feedUrl ? (
                <code className="panoptic-rule-feed-url" data-testid="T_PANOPTIC_WEB_168_rule_feed_url">
                  {feedUrl}
                </code>
              ) : null}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function NvrCameraMatrix({ matrix, orgId, collapsed, onToggle, onInspect }) {
  return (
    <section className="panoptic-nvr-matrix" data-testid="T_PANOPTIC_WEB_127_nvr_matrix">
      <button
        type="button"
        className="panoptic-nvr-head"
        onClick={onToggle}
        aria-expanded={!collapsed}
        data-testid="T_PANOPTIC_WEB_127_nvr_matrix_toggle"
      >
        <div>
          <strong>NVR Camera Matrix</strong>
          <span>{matrix.label}</span>
        </div>
        <StatusPill status={matrix.status}>{matrix.stale ? 'stale' : matrix.status}</StatusPill>
        <span>{matrix.cameras.length} camera{matrix.cameras.length === 1 ? '' : 's'}</span>
        <span aria-hidden="true">{collapsed ? '+' : '-'}</span>
      </button>
      {!collapsed && (
        <div className="panoptic-camera-grid panoptic-nvr-grid">
          {matrix.cameras.map((camera) => (
            <CameraTile key={`${matrix.id}-${camera.id}-${camera.feedId}`} camera={camera} orgId={orgId} onInspect={onInspect} />
          ))}
        </div>
      )}
    </section>
  );
}

function CommandPalette({ open, onClose, commands }) {
  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release
  // (both mousedown and mouseup on the backdrop itself); a text-selection drag
  // from inside the palette to outside it must not close it. Declared
  // unconditionally (rules of hooks) ahead of the `!open` early return below.
  const backdropDismissHandlers = useDismissOnOutsideClick(onClose);

  if (!open) {
    return null;
  }

  return (
    <div className="panoptic-modal-backdrop" role="presentation" {...backdropDismissHandlers}>
      <section className="panoptic-command-palette" role="dialog" aria-modal="true" aria-label="PanOptic command palette" onMouseDown={(event) => event.stopPropagation()}>
        <div className="panoptic-command-input">
          <span>Search</span>
          <input autoFocus aria-label="Command search" placeholder="Find cameras, reviews, or actions" />
        </div>
        <div className="panoptic-command-list">
          {commands.map((command) => (
            <button key={command.title} type="button" onClick={command.action}>
              <strong>{command.title}</strong>
              <span>{command.detail}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function PanopticDrawer({ title, open, onClose, children }) {
  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release
  // (both mousedown and mouseup on the backdrop itself); a text-selection drag
  // from inside the drawer to outside it must not close it. Declared
  // unconditionally (rules of hooks) ahead of the `!open` early return below.
  const backdropDismissHandlers = useDismissOnOutsideClick(onClose);

  if (!open) {
    return null;
  }

  return (
    <div className="panoptic-drawer-backdrop" role="presentation" {...backdropDismissHandlers}>
      <aside className="panoptic-drawer" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <h2>{title}</h2>
          <button type="button" className="panoptic-icon-button" onClick={onClose} aria-label={`Close ${title}`}>×</button>
        </header>
        {children}
      </aside>
    </div>
  );
}

function PanopticAppLauncher() {
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState('popover');
  const buttonRef = useRef(null);
  const tileRefs = useRef([]);
  const links = useMemo(() => getPanopticLauncherLinks(), []);

  const syncLayout = useCallback(() => {
    setLayout(window.matchMedia('(max-width: 640px)').matches ? 'bottom-sheet' : 'popover');
  }, []);

  const closeLauncher = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => buttonRef.current?.focus());
    }
  }, []);

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release
  // (both mousedown and mouseup on the backdrop itself); a text-selection drag
  // from inside the launcher panel to outside it must not close it.
  const launcherBackdropDismissHandlers = useDismissOnOutsideClick(() => closeLauncher());

  const moveFocus = useCallback((step) => {
    const tiles = tileRefs.current.filter(Boolean);
    const activeIndex = tiles.indexOf(document.activeElement);
    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = (currentIndex + step + tiles.length) % tiles.length;
    tiles[nextIndex]?.focus();
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    syncLayout();
    window.requestAnimationFrame(() => tileRefs.current[0]?.focus());
    window.addEventListener('resize', syncLayout);

    const onKeyDown = (event) => {
      const tiles = tileRefs.current.filter(Boolean);
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLauncher();
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        moveFocus(1);
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveFocus(-1);
        return;
      }
      if (event.key === 'Tab' && tiles.length) {
        const firstTile = tiles[0];
        const lastTile = tiles[tiles.length - 1];
        if (event.shiftKey && document.activeElement === firstTile) {
          event.preventDefault();
          lastTile.focus();
        } else if (!event.shiftKey && document.activeElement === lastTile) {
          event.preventDefault();
          firstTile.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', syncLayout);
    };
  }, [closeLauncher, moveFocus, open, syncLayout]);

  return (
    <div className="panoptic-app-launcher" data-testid={PANOPTIC_APP_LAUNCHER_TEST_IDS.root}>
      <button
        ref={buttonRef}
        className="panoptic-app-launcher-button"
        type="button"
        aria-label="Open PanOptic app launcher"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="panoptic-app-launcher-panel"
        data-testid={PANOPTIC_APP_LAUNCHER_TEST_IDS.button}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="panoptic-app-launcher-icon" aria-hidden="true">
          <span /><span /><span />
          <span /><span /><span />
          <span /><span /><span />
        </span>
      </button>
      {open && (
        <>
          <div
            className="panoptic-app-launcher-backdrop"
            data-testid={PANOPTIC_APP_LAUNCHER_TEST_IDS.backdrop}
            role="presentation"
            {...launcherBackdropDismissHandlers}
          />
          <nav
            id="panoptic-app-launcher-panel"
            className="panoptic-app-launcher-panel"
            role="menu"
            aria-label="PanOptic app launcher"
            data-layout={layout}
            data-testid={PANOPTIC_APP_LAUNCHER_TEST_IDS.panel}
          >
            <div className="panoptic-app-launcher-grid">
              {links.map((link, index) => (
                <a
                  key={link.label}
                  ref={(node) => { tileRefs.current[index] = node; }}
                  className={`panoptic-app-launcher-tile ${link.current ? 'is-current' : ''}`}
                  href={link.href}
                  role="menuitem"
                  aria-current={link.current ? 'page' : undefined}
                  data-testid={link.testId}
                  onClick={() => closeLauncher(false)}
                >
                  <span aria-hidden="true">{link.label[0]}</span>
                  <span>{link.label}</span>
                </a>
              ))}
            </div>
          </nav>
        </>
      )}
    </div>
  );
}

function PanopticCockpit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [state, setState] = useState({
    loading: true,
    degraded: false,
    error: '',
    cameras: [],
    reviews: [],
    runtime: runtimeFallback,
    source: 'loading',
    sourceView: buildPanopticSourceView(),
    orgId: null,
    linkState: 'unknown',
    linkDetail: 'Checking link',
    nvrMatrices: [],
    activeWatchRules: {
      loading: true,
      error: '',
      stale: false,
      source: 'loading',
      rules: [],
    },
  });
  const [site, setSite] = useState('Studio A');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawer, setDrawer] = useState(searchParams.get('panel') || '');
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [updatesAvailable, setUpdatesAvailable] = useState(false);
  const [feedRuleStates, setFeedRuleStates] = useState({});
  const [collapsedNvrs, setCollapsedNvrs] = useState({});

  // MP004-D006 §5.1.2 / M004-F0103: the page-level organisation selector
  // state. `organisations` holds the resolved allowed-organisation list,
  // filtered the same way /app/admin/rooms filters its own selector list;
  // `selectedOrgId` is the explicit selection every subsequent cockpit
  // request uses, persisted as `?org=<id>` shallow route state.
  const [organisations, setOrganisations] = useState([]);
  const [organisationsReady, setOrganisationsReady] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(searchParams.get('org') || '');

  useEffect(() => {
    let active = true;
    const resolveOrganisationScope = async () => {
      try {
        const orgResponse = await organisationApi.getMine();
        const rawOrganisations = asArray(extractData(orgResponse), ['organisations']);
        if (!active) return;
        setOrganisations(selectPanopticOrganisations(rawOrganisations, user));
        setSelectedOrgId((current) => resolvePanopticOrganisationSelection(
          current || searchParams.get('org'),
          rawOrganisations,
          user,
        ));
      } catch (err) {
        if (!active) return;
        setOrganisations([]);
        setSelectedOrgId('');
      } finally {
        if (active) setOrganisationsReady(true);
      }
    };
    resolveOrganisationScope();
    return () => { active = false; };
    // Re-resolve only when the session identity changes, not on every render.
  }, [user?.id, user?.role]);

  // Keep the selection in sync with the URL (a shared link, or the browser
  // back/forward buttons), fail-closed against the resolved allowed list —
  // never an out-of-scope, stale, or forged organisation.
  useEffect(() => {
    const urlOrgId = searchParams.get('org');
    if (!urlOrgId || organisations.length === 0) {
      return;
    }
    setSelectedOrgId((current) => {
      const resolved = resolvePanopticOrganisationSelection(urlOrgId, organisations, user);
      return current === resolved ? current : resolved;
    });
  }, [searchParams, organisations, user]);

  // Reflect the resolved selection back into the URL as `?org=<id>`,
  // consistent with the `?feed=<feedId>` shallow-route contract in §5.4.4.
  useEffect(() => {
    if (!selectedOrgId || searchParams.get('org') === selectedOrgId) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('org', selectedOrgId);
    setSearchParams(next);
  }, [selectedOrgId]);

  const selectOrganisation = (nextOrgId) => {
    const resolved = resolvePanopticOrganisationSelection(nextOrgId, organisations, user);
    setSelectedOrgId(resolved);
    const next = new URLSearchParams(searchParams);
    next.set('org', resolved);
    setSearchParams(next);
  };

  const loadCockpit = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const [summaryResult, cameraHealthResult, devicesResult, anomaliesResult] = await Promise.allSettled([
        networkApi.getHealthSummary(),
        networkApi.getCameraHealth(),
        networkApi.getDevices({ limit: 100, offset: 0 }),
        networkApi.getAnomalies({ limit: 8 }),
      ]);

      const rejected = [summaryResult, cameraHealthResult, devicesResult, anomaliesResult]
        .find((result) => result.status === 'rejected');
      const summaryPayload = summaryResult.status === 'fulfilled' ? extractData(summaryResult.value) : null;
      const cameraHealthPayload = cameraHealthResult.status === 'fulfilled' ? extractData(cameraHealthResult.value) : null;
      const devicesPayload = devicesResult.status === 'fulfilled' ? extractData(devicesResult.value) : null;
      const anomaliesPayload = anomaliesResult.status === 'fulfilled' ? extractData(anomaliesResult.value) : null;

      // MP004-D006 §5.1.2 / MP004-F0423: the org is the explicit page-level
      // selection resolved above (never a re-derived first-with-id pick), so
      // matrix snapshot, watch-rule, and NVR requests all key off the same
      // explicitly selected organisation as the rest of the cockpit.
      const orgId = selectedOrgId || null;
      let feedsPayload = null;
      let panopticFeedsPayload = null;
      let activeWatchRules = {
        loading: false,
        error: '',
        stale: false,
        source: orgId ? 'server-link' : 'unqueried',
        rules: [],
      };
      let linkState = 'unknown';
      let linkDetail = 'PanOptic link state unknown';
      let feedsErrorForState = null;
      if (orgId) {
        try {
          feedsPayload = extractData(await securityCamerasApi.getFeeds({ org_id: orgId }));
        } catch (feedsError) {
          feedsPayload = null;
        }
        try {
          panopticFeedsPayload = extractData(await panoptesApi.getOrgFeeds(orgId));
        } catch (feedsError) {
          feedsErrorForState = feedsError;
        }
        try {
          activeWatchRules = normalizeActiveWatchRuleState(extractData(await panoptesApi.getActiveWatchRules(orgId)));
        } catch (watchRulesError) {
          activeWatchRules = {
            loading: false,
            error: buildWatchRuleError(watchRulesError),
            stale: false,
            source: 'server-link',
            rules: [],
          };
        }
      }
      const connectionState = getPanopticConnectionState({
        feedsPayload: panopticFeedsPayload,
        activeWatchRules,
        feedsError: feedsErrorForState,
        degraded: Boolean(rejected),
      });
      linkState = connectionState.linkState;
      linkDetail = connectionState.linkDetail;

      const cameras = mergePanopticFeedCameras(
        attachSecurityFeeds(mapCameraPayload(cameraHealthPayload, devicesPayload), feedsPayload),
        panopticFeedsPayload,
      );
      const nvrMatrices = buildNvrCameraMatrices(panopticFeedsPayload);
      const effectiveCameras = cameras.length > 0 ? cameras : sampleCameras;

      setState({
        loading: false,
        degraded: Boolean(rejected),
        error: rejected ? buildErrorText(rejected.reason) : '',
        cameras: effectiveCameras,
        reviews: mapReviewPayload(anomaliesPayload, effectiveCameras),
        runtime: mapRuntimePayload(summaryPayload, cameraHealthPayload),
        source: cameras.length > 0 ? 'live' : 'operational-template',
        sourceView: buildPanopticSourceView(panopticFeedsPayload),
        orgId,
        linkState,
        linkDetail,
        nvrMatrices,
        activeWatchRules,
      });
    } catch (error) {
      setState({
        loading: false,
        degraded: true,
        error: buildErrorText(error),
        cameras: sampleCameras,
        reviews: sampleReviews,
        runtime: runtimeFallback,
        source: 'degraded-template',
        sourceView: buildPanopticSourceView(),
        orgId: null,
        linkState: 'degraded',
        linkDetail: 'PanOptic link telemetry is degraded',
        nvrMatrices: [],
        activeWatchRules: {
          loading: false,
          error: buildWatchRuleError(error),
          stale: false,
          source: 'server-link',
          rules: [],
        },
      });
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (!organisationsReady) {
      return;
    }
    loadCockpit();
  }, [loadCockpit, organisationsReady]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (drawer === 'camera' && selectedCamera?.feedId) {
        setUpdatesAvailable(true);
        return;
      }
      loadCockpit();
    }, 90000);
    return () => window.clearInterval(timer);
  }, [drawer, loadCockpit, selectedCamera]);

  useEffect(() => {
    if (!state.activeWatchRules?.error && !state.activeWatchRules?.stale) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      loadCockpit();
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [loadCockpit, state.activeWatchRules?.error, state.activeWatchRules?.stale]);

  useEffect(() => {
    const nextPanel = searchParams.get('panel') || '';
    const cameraId = searchParams.get('camera');
    const nextDialog = searchParams.get('dialog');
    if (nextPanel) {
      setDrawer(nextPanel);
    }
    if (nextDialog === 'discovery') {
      setDrawer('discovery');
    }
    if (cameraId) {
      const camera = state.cameras.find((item) => String(item.id) === cameraId);
      if (camera) {
        setSelectedCamera(camera);
        setDrawer('camera');
      }
    }
  }, [searchParams, state.cameras]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (event.key === 'Escape') {
        setPaletteOpen(false);
        setDrawer('');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const metrics = useMemo(() => {
    const cameras = state.cameras;
    const online = cameras.filter((camera) => normalizeStatus(camera.status) === 'online').length;
    const offline = cameras.filter((camera) => ['offline', 'degraded', 'unknown'].includes(normalizeStatus(camera.status))).length;
    const alerts = state.reviews.filter((review) => ['critical', 'alert', 'warning'].includes(normalizeStatus(review.severity))).length;
    return [
      { label: 'Cameras', value: `${formatCount(online)}/${formatCount(cameras.length)}`, detail: `${offline} need attention`, action: () => openDrawer('cameras') },
      { label: 'Alerts', value: formatCount(alerts), detail: 'active review items', action: () => openDrawer('reviews') },
      { label: 'Detections', value: formatCount(Math.max(12, state.reviews.length * 7)), detail: 'today', action: () => openDrawer('reviews') },
      { label: 'Detector', value: state.runtime.detectorState, detail: state.runtime.accelerator, action: () => openDrawer('detector') },
      { label: 'Sync', value: state.runtime.sync, detail: state.runtime.discovery, action: () => openDrawer('sync') },
    ];
  }, [state.cameras, state.reviews, state.runtime]);

  const loadFeedWatchRules = useCallback(async (camera) => {
    if (!camera?.feedId || !state.orgId) {
      return;
    }
    const feedId = String(camera.feedId);
    setFeedRuleStates((current) => ({
      ...current,
      [feedId]: {
        ...(current[feedId] || {}),
        loading: true,
        error: '',
      },
    }));
    try {
      const response = await panoptesApi.getFeedWatchRules(state.orgId, feedId);
      const payload = extractData(response);
      const rules = normalizeWatchRulePayload(payload);
      setFeedRuleStates((current) => ({
        ...current,
        [feedId]: {
          loading: false,
          mutating: false,
          error: '',
          stale: Boolean(payload?.stale || payload?.cached),
          rules,
        },
      }));
    } catch (error) {
      setFeedRuleStates((current) => ({
        ...current,
        [feedId]: {
          ...(current[feedId] || {}),
          loading: false,
          mutating: false,
          error: buildWatchRuleError(error),
          needsRefresh: error?.response?.status === 409,
          rules: current[feedId]?.rules || [],
        },
      }));
    }
  }, [state.orgId]);

  const mutateFeedRule = useCallback(async (action, rule) => {
    if (!selectedCamera?.feedId || !state.orgId) {
      return;
    }
    const feedId = String(selectedCamera.feedId);
    setFeedRuleStates((current) => ({
      ...current,
      [feedId]: {
        ...(current[feedId] || {}),
        mutating: true,
        error: '',
      },
    }));
    try {
      if (action === 'add') {
        await panoptesApi.createFeedWatchRule(state.orgId, feedId, {
          rule_type: 'out_of_hours_object_watch',
          label: 'Out of hours object watch',
          severity: 'critical',
          ensure_frigate_feed: true,
          feed_id: feedId,
        });
        setActionMessage('Out of hours object watch requested for the selected feed');
      } else {
        await panoptesApi.deleteFeedWatchRule(state.orgId, feedId, rule.id);
        setActionMessage('Out of hours object watch removal requested');
      }
      await loadFeedWatchRules(selectedCamera);
      await loadCockpit();
    } catch (error) {
      setFeedRuleStates((current) => ({
        ...current,
        [feedId]: {
          ...(current[feedId] || {}),
          mutating: false,
          error: buildWatchRuleError(error),
        },
      }));
    }
  }, [loadCockpit, loadFeedWatchRules, selectedCamera, state.orgId]);

  useEffect(() => {
    if (drawer === 'camera' && selectedCamera?.feedId) {
      loadFeedWatchRules(selectedCamera);
    }
  }, [drawer, loadFeedWatchRules, selectedCamera]);

  const openDrawer = (nextDrawer) => {
    setDrawer(nextDrawer);
    const next = new URLSearchParams(searchParams);
    next.set('panel', nextDrawer);
    if (nextDrawer !== 'camera') {
      next.delete('camera');
    }
    setSearchParams(next);
  };

  const closeDrawer = () => {
    setDrawer('');
    setSelectedCamera(null);
    const next = new URLSearchParams(searchParams);
    next.delete('panel');
    next.delete('camera');
    next.delete('dialog');
    setSearchParams(next);
  };

  const inspectCamera = (camera) => {
    setSelectedCamera(camera);
    setDrawer('camera');
    const next = new URLSearchParams(searchParams);
    next.set('camera', String(camera.id));
    next.set('panel', 'camera');
    setSearchParams(next);
    loadFeedWatchRules(camera);
  };

  const openTrackingReview = (selection = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'tracking');
    next.delete('panel');
    next.delete('camera');
    if (selection.type && selection.id) {
      next.set(selection.type, selection.id);
    }
    setDrawer('');
    setSearchParams(next);
  };

  const runSync = async () => {
    setActionMessage('Sync check started');
    try {
      await networkApi.triggerScan({ source: 'panoptic-cockpit', scope: site });
      setActionMessage('Sync check requested on the live backend');
      await loadCockpit();
    } catch (error) {
      setActionMessage(buildErrorText(error));
    }
  };

  const refreshSourceState = async () => {
    setActionMessage('Refreshing PanOptic source state');
    await loadCockpit();
    if (selectedCamera?.feedId) {
      await loadFeedWatchRules(selectedCamera);
    }
    setUpdatesAvailable(false);
    setActionMessage('PanOptic source state refreshed');
  };

  const commands = [
    { title: 'Review active alerts', detail: 'Open the review rail detail', action: () => { setPaletteOpen(false); openDrawer('reviews'); } },
    { title: 'Review unusual behaviour', detail: 'Open recognition review', action: () => { setPaletteOpen(false); openTrackingReview(); } },
    { title: 'Find familiar vehicles', detail: 'Search recurring identity clusters', action: () => { setPaletteOpen(false); openTrackingReview(); } },
    { title: 'Review unknown people', detail: 'Open behaviour inbox', action: () => { setPaletteOpen(false); openTrackingReview(); } },
    { title: 'Run discovery', detail: 'Open onboarding sources', action: () => { setPaletteOpen(false); openDrawer('discovery'); } },
    { title: 'Inspect detector', detail: 'Open runtime diagnostics', action: () => { setPaletteOpen(false); openDrawer('detector'); } },
    { title: 'Open estate viewer', detail: 'Dense camera inventory', action: () => { setPaletteOpen(false); setSearchParams({ view: 'estate' }); } },
  ];

  const hasNoCameras = state.source === 'empty' || (state.cameras.length === 0 && !state.loading);
  const estateMode = searchParams.get('view') === 'estate';
  const trackingMode = searchParams.get('view') === 'tracking';
  const linkStateView = getPanopticLinkStateView(state.linkState, state.degraded);
  const isRestricted = state.linkState === 'restricted' || /protected|authorization/i.test(state.error || actionMessage);
  const showOrganisationSelector = shouldShowPanopticOrganisationSelector(organisations, user);
  const organisationScopeEmpty = organisationsReady && organisations.length === 0;
  const selectedFeedRuleState = selectedCamera?.feedId ? feedRuleStates[String(selectedCamera.feedId)] : null;
  const recognitionSelection = ['track', 'cluster', 'candidate', 'anomaly', 'settings']
    .map((type) => ({ type, id: searchParams.get(type) }))
    .find((item) => item.id) || { type: '', id: '' };

  return (
    <Layout>
      <section className="panoptic-cockpit" data-testid="panoptic-cockpit" aria-labelledby="panoptic-title">
        <header className="panoptic-topbar" data-testid="panoptic-header">
          <div className="panoptic-brand">
            <span className="panoptic-mark" aria-hidden="true">P</span>
            <div>
              <p>StudioAEye</p>
              <h1 id="panoptic-title">PanOptic</h1>
            </div>
          </div>

          {showOrganisationSelector && (
            <label className="panoptic-site-select">
              <span>Organisation</span>
              <select
                value={selectedOrgId}
                onChange={(event) => selectOrganisation(event.target.value)}
                aria-label="PanOptic organisation"
                data-testid="panoptic-organisation-selector"
              >
                {organisations.map((organisation) => (
                  <option key={organisation.id} value={organisation.id}>{organisation.name}</option>
                ))}
              </select>
            </label>
          )}
          {organisationScopeEmpty && (
            <p className="form-error" role="alert" data-testid="panoptic-organisation-scope-empty">
              No administered organisation is available for PanOptic.
            </p>
          )}

          <label className="panoptic-site-select">
            <span>Source</span>
            <select
              value={state.sourceView.id}
              onChange={() => setSite(state.sourceView.label)}
              aria-label="PanOptic source"
              data-testid="T_PANOPTIC_WEB_170_source_selector"
            >
              <option value={state.sourceView.id}>{state.sourceView.label}</option>
            </select>
          </label>

          <button className="panoptic-command-button" type="button" onClick={() => setPaletteOpen(true)} data-testid="panoptic-command-entry">
            <span>Search command</span>
            <kbd>Ctrl K</kbd>
          </button>

          <div className="panoptic-topbar-actions">
            <StatusPill status={linkStateView.status}>
              <span data-testid="panoptic-link-state">
                <span data-testid="T_PANOPTIC_WEB_171_connection_state">{linkStateView.label}</span>
              </span>
            </StatusPill>
            <button type="button" className="panoptic-secondary-button" onClick={runSync} data-testid="panoptic-sync-entry">Sync</button>
            <button type="button" className="panoptic-secondary-button" onClick={refreshSourceState} data-testid="T_PANOPTIC_WEB_172_refresh_source">Refresh</button>
            <button type="button" className="panoptic-icon-button" onClick={() => openDrawer('settings')} aria-label="Open PanOptic settings" data-testid="panoptic-settings-entry">⚙</button>
            <PanopticAppLauncher />
          </div>
        </header>

        {(state.degraded || actionMessage) && (
          <div className="panoptic-alert-banner" data-testid="panoptic-alert-banner">
            <strong>{isRestricted ? 'Authorization refresh required' : state.degraded ? 'Operational attention needed' : 'Action update'}</strong>
            <span>{actionMessage || state.error}</span>
            <button
              type="button"
              onClick={isRestricted ? refreshSourceState : () => openDrawer('detector')}
              data-testid={isRestricted ? 'T_PANOPTIC_WEB_172_authorization_refresh' : undefined}
            >
              {isRestricted ? 'Refresh authorization' : 'Inspect runtime'}
            </button>
          </div>
        )}

        <div className="panoptic-health-strip" data-testid="panoptic-health-strip">
          {metrics.map((metric) => (
            <PanopticMetric
              key={metric.label}
              label={metric.label}
              value={metric.value}
              detail={metric.detail}
              onClick={metric.action}
            />
          ))}
        </div>

        <div className={`panoptic-body ${estateMode ? 'show-estate' : ''} ${trackingMode ? 'show-tracking' : ''}`}>
          <div className="panoptic-main-panel">
            <div className="panoptic-section-head">
              <div>
                <h2>{trackingMode ? 'Recognition Review' : estateMode ? 'Camera Estate' : 'Camera Matrix'}</h2>
                <p>{trackingMode ? 'Review unusual behaviour, recurring identities, and admin feedback.' : estateMode ? 'Dense operational view for scanning the full authorized estate.' : 'Authorized camera summaries with privacy-safe media placeholders.'}</p>
              </div>
              <div className="panoptic-section-actions">
                {trackingMode ? (
                  <button type="button" className="panoptic-secondary-button" onClick={() => setSearchParams({})} data-testid="T_PANOPTIC_WEB_137_cockpit_nav_entry">
                    Cockpit view
                  </button>
                ) : (
                  <>
                    <button type="button" className="panoptic-secondary-button" onClick={() => openTrackingReview()} data-testid="T_PANOPTIC_WEB_137_tracking_nav_entry">
                      Recognition review
                    </button>
                    <button type="button" className="panoptic-secondary-button" onClick={() => setSearchParams(estateMode ? {} : { view: 'estate' })} data-testid="panoptic-estate-entry">
                      {estateMode ? 'Cockpit view' : 'Estate viewer'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {trackingMode && (
              <PanopticRecognitionReview
                orgId={state.orgId}
                site={site}
                api={panoptesApi}
                initialSelection={recognitionSelection}
                onSelectionChange={(selection) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('view', 'tracking');
                  ['track', 'cluster', 'candidate', 'anomaly', 'settings'].forEach((key) => next.delete(key));
                  if (selection.type && selection.id) {
                    next.set(selection.type, selection.id);
                  }
                  setSearchParams(next);
                }}
              />
            )}

            {!trackingMode && state.loading && (
              <div className="panoptic-empty-state" data-testid="panoptic-loading-state">
                <strong>Loading PanOptic telemetry</strong>
                <span>Camera, detector, and review state are being requested from the live backend.</span>
              </div>
            )}

            {!trackingMode && !state.loading && hasNoCameras && (
              <div className="panoptic-empty-state" data-testid="panoptic-empty-state">
                <strong>No cameras are onboarded</strong>
                <span>Start discovery or add a camera manually to build the PanOptic estate.</span>
                <button type="button" onClick={() => openDrawer('discovery')}>Run discovery</button>
              </div>
            )}

            {!trackingMode && !state.loading && !estateMode && state.cameras.length > 0 && (
              <>
                <div className="panoptic-camera-grid" data-testid="panoptic-camera-matrix">
                  {state.cameras.map((camera) => (
                    <CameraTile key={camera.id} camera={camera} orgId={state.orgId} onInspect={inspectCamera} />
                  ))}
                </div>
                {state.nvrMatrices.map((matrix) => (
                  <NvrCameraMatrix
                    key={matrix.id}
                    matrix={matrix}
                    orgId={state.orgId}
                    collapsed={Boolean(collapsedNvrs[matrix.id])}
                    onToggle={() => setCollapsedNvrs((current) => ({ ...current, [matrix.id]: !current[matrix.id] }))}
                    onInspect={inspectCamera}
                  />
                ))}
              </>
            )}

            {!trackingMode && !state.loading && estateMode && (
              <div className="panoptic-estate-table" data-testid="panoptic-estate-lite">
                <div className="panoptic-estate-row panoptic-estate-head">
                  <span>Camera</span>
                  <span>Room</span>
                  <span>Status</span>
                  <span>Last activity</span>
                  <span>Action</span>
                </div>
                {state.cameras.map((camera) => (
                  <div className="panoptic-estate-row" key={`estate-${camera.id}`}>
                    <strong>{camera.name}</strong>
                    <span>{camera.room}</span>
                    <StatusPill status={camera.status}>{camera.status}</StatusPill>
                    <span>{camera.lastSeen}</span>
                    <button type="button" onClick={() => inspectCamera(camera)}>Inspect</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!trackingMode && <aside className="panoptic-review-rail" data-testid="panoptic-review-rail">
            <div className="panoptic-section-head compact">
              <div>
                <h2>Review</h2>
                <p>{site}</p>
              </div>
              <button type="button" onClick={() => openTrackingReview()}>Open</button>
            </div>
            {state.reviews.length === 0 ? (
              <div className="panoptic-inline-state">
                <strong>No alerts</strong>
                <span>Review rail is quiet for this scope.</span>
              </div>
            ) : state.reviews.map((review) => (
              <button className="panoptic-review-item" type="button" key={review.id} onClick={() => openTrackingReview({ type: 'anomaly', id: review.id })}>
                <StatusPill status={review.severity}>{review.severity}</StatusPill>
                <strong>{review.title}</strong>
                <span>{review.camera} · {review.age} · {review.state}</span>
              </button>
            ))}
            <ActiveWatchRulesSummary
              activeRuleState={state.activeWatchRules}
              feedRuleStates={feedRuleStates}
              onRefresh={refreshSourceState}
              orgId={state.orgId}
            />
          </aside>}
        </div>

        <footer className="panoptic-runtime-band" data-testid="panoptic-runtime-band">
          <div>
            <span>Detector</span>
            <strong>{state.runtime.detector}</strong>
            <StatusPill status={state.runtime.detectorState}>{state.runtime.detectorState}</StatusPill>
          </div>
          <div>
            <span>Accelerator</span>
            <strong>{state.runtime.accelerator}</strong>
          </div>
          <div>
            <span>Discovery</span>
            <strong>{state.runtime.discovery}</strong>
          </div>
          <div>
            <span>Audit</span>
            <strong>{state.runtime.audit}</strong>
          </div>
        </footer>

        <nav className="panoptic-mobile-actions" aria-label="PanOptic mobile actions" data-testid="panoptic-mobile-actions">
          <button type="button" onClick={() => openTrackingReview()}>Review</button>
          <button type="button" onClick={() => setPaletteOpen(true)}>Search</button>
          <button type="button" onClick={() => openDrawer('discovery')}>Discover</button>
          <button type="button" onClick={() => openDrawer('detector')}>Runtime</button>
        </nav>
      </section>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={commands} />

      <PanopticDrawer title="Camera detail" open={drawer === 'camera'} onClose={closeDrawer}>
        {selectedCamera ? (
          <div className="panoptic-drawer-stack" data-testid="panoptic-camera-drawer">
            <button
              type="button"
              className="panoptic-secondary-button"
              onClick={refreshSourceState}
              data-testid="T_PANOPTIC_WEB_172_refresh_source_drawer"
            >
              Refresh source state
            </button>
            <StatusPill status={selectedCamera.status}>{selectedCamera.status}</StatusPill>
            {updatesAvailable && (
              <div className="panoptic-inline-warning" data-testid="T_PANOPTIC_WEB_172_update_available">
                <strong>Updates available</strong>
                <span>Source state changed while this drawer was open.</span>
                <button type="button" onClick={refreshSourceState} data-testid="T_PANOPTIC_WEB_172_apply_update">Apply refresh</button>
              </div>
            )}
            <h3>{selectedCamera.name}</h3>
            <p>{selectedCamera.room} · {selectedCamera.site}</p>
            <dl>
              <div><dt>Last activity</dt><dd>{selectedCamera.lastSeen}</dd></div>
              <div><dt>Media</dt><dd>{selectedCamera.unauthorized ? 'Restricted by authorization policy' : 'Privacy-safe preview only'}</dd></div>
              <div><dt>Labels</dt><dd>{(selectedCamera.labels || []).join(', ') || 'No labels'}</dd></div>
              <div><dt>Feed</dt><dd>{selectedCamera.feedId || 'No linked feed'}</dd></div>
              <div><dt>NVR channel</dt><dd>{selectedCamera.nvrChannel || 'Standalone camera'}</dd></div>
            </dl>
            <ActiveWatchRulesCard
              camera={selectedCamera}
              ruleState={selectedFeedRuleState}
              onAddRule={() => mutateFeedRule('add')}
              onRemoveRule={(rule) => mutateFeedRule('remove', rule)}
              onRefresh={() => loadFeedWatchRules(selectedCamera)}
            />
            <button type="button" className="panoptic-primary-button">Open review context</button>
          </div>
        ) : (
          <p>No camera selected.</p>
        )}
      </PanopticDrawer>

      <PanopticDrawer title="Review filters" open={drawer === 'reviews'} onClose={closeDrawer}>
        <div className="panoptic-drawer-stack">
          <button type="button">New and critical</button>
          <button type="button">Detector warnings</button>
          <button type="button">Closed today</button>
          <p>Review filters keep the cockpit scoped to {site}.</p>
        </div>
      </PanopticDrawer>

      <PanopticDrawer title="Discovery" open={drawer === 'discovery' || drawer === 'cameras'} onClose={closeDrawer}>
        <div className="panoptic-drawer-stack" data-testid="panoptic-discovery-drawer">
          <button type="button">ONVIF discovery</button>
          <button type="button">NVR import</button>
          <button type="button">Frigate import</button>
          <button type="button">Manual camera</button>
          <p>Discovery launches from the cockpit and returns camera state to the matrix.</p>
        </div>
      </PanopticDrawer>

      <PanopticDrawer title="Detector runtime" open={drawer === 'detector' || drawer === 'sync'} onClose={closeDrawer}>
        <div className="panoptic-drawer-stack" data-testid="panoptic-detector-drawer">
          <dl>
            <div><dt>Server Link</dt><dd>{state.linkDetail}</dd></div>
            <div><dt>Source</dt><dd>{state.sourceView.label}</dd></div>
            <div><dt>Detector</dt><dd>{state.runtime.detector}</dd></div>
            <div><dt>State</dt><dd>{state.runtime.detectorState}</dd></div>
            <div><dt>Accelerator</dt><dd>{state.runtime.accelerator}</dd></div>
            <div><dt>Sync</dt><dd>{state.runtime.sync}</dd></div>
          </dl>
          <button type="button" onClick={runSync}>Validate sync</button>
        </div>
      </PanopticDrawer>

      <PanopticDrawer title="PanOptic settings" open={drawer === 'settings'} onClose={closeDrawer}>
        <div className="panoptic-drawer-stack" data-testid="panoptic-settings-drawer">
          <button type="button">Camera policy</button>
          <button type="button">Detector policy</button>
          <button type="button">Notification policy</button>
          <p>Settings open without leaving the cockpit route.</p>
        </div>
      </PanopticDrawer>
    </Layout>
  );
}

export default PanopticCockpit;
